import {useMemo, useState} from 'react';
import type {QueryResult} from '@apollo/react-common';
import {useMutation, useQuery} from '@apollo/react-hooks';
import gql from 'graphql-tag';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {
  EXPIRED_MAGIC_LINK_PATH,
  PROGRESS_PATH,
  REQUEST_SIGN_IN_LINK_PATH,
  SELF_ASSIGN_PATH,
} from '../../paths';
import {
  SelfAssignSource,
  type AccountIdentityQuery,
  type AccountSessionCreateMutation,
  type AccountSessionCreateMutationVariables,
  type MagicLinkDriverSessionCreateMutation,
  type MagicLinkDriverSessionCreateMutationVariables,
  type MeQuery,
} from '../graphql/types';
import {MagicLinkDriverSessionCreate, Me} from '../graphql';
import {useFederationApiEppoFlag} from '../eppo';
import configureUserMonitoring from './configureUserMonitoring';

const MAGIC_LINK_TOKEN_QUERY_PARAM = 't';

export const IdentityQuery = gql`
  query AccountIdentityQuery {
    me {
      driverAccount {
        id
        firstName
        lastName
        phone
        smsOptInStatus
      }
    }
  }
`;

export const SessionCreateMutation = gql`
  mutation AccountSessionCreateMutation($input: DriverAccountSessionCreateInput!) {
    accountSessionCreate: driverAccountSessionCreate(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`;

const getAdaptedMeQuery = (data?: AccountIdentityQuery | null): MeQuery | undefined => {
  if (!data) {
    return undefined;
  }

  if (!data.me || !data.me.driverAccount) {
    return {me: null};
  }

  return {
    me: {
      __typename: 'Driver',
      firstName: data.me.driverAccount.firstName,
      id: data.me.driverAccount.id,
      lastName: data.me.driverAccount.lastName,
      phone: data.me.driverAccount.phone,
      smsOptInStatus: data.me.driverAccount.smsOptInStatus,
    },
  };
};

const getAdaptedAccountSessionCreate = (
  data?: AccountSessionCreateMutation | null
): MagicLinkDriverSessionCreateMutation | undefined => {
  if (!data) {
    return undefined;
  }

  if (!data.accountSessionCreate) {
    return {magicLinkDriverSessionCreate: null};
  }

  return {
    magicLinkDriverSessionCreate: {
      __typename: 'MagicLinkDriverSessionCreatePayload',
      success: !data.accountSessionCreate.userErrors.length,
    },
  };
};

type UseAccountSessionCreateMutationOptions = Pick<
  NonNullable<
    Parameters<
      typeof useMutation<
        MagicLinkDriverSessionCreateMutation,
        MagicLinkDriverSessionCreateMutationVariables
      >
    >[1]
  >,
  'onCompleted' | 'onError'
>;

type UseAccountSessionCreateMutationResult =
  | ReturnType<
      typeof useMutation<
        MagicLinkDriverSessionCreateMutation,
        MagicLinkDriverSessionCreateMutationVariables
      >
    >
  | ReturnType<
      typeof useMutation<AccountSessionCreateMutation, AccountSessionCreateMutationVariables>
    >;

const useAccountSessionCreateMutation = (
  options: UseAccountSessionCreateMutationOptions
): UseAccountSessionCreateMutationResult => {
  const featureFlag = useFederationApiEppoFlag();
  const [executeLegacyMutation, legacyMutationResult] = useMutation<
    MagicLinkDriverSessionCreateMutation,
    MagicLinkDriverSessionCreateMutationVariables
  >(MagicLinkDriverSessionCreate, options);
  const [executeMutation, mutationResult] = useMutation<
    AccountSessionCreateMutation,
    AccountSessionCreateMutationVariables
  >(SessionCreateMutation, {
    ...options,
    onCompleted: (data: AccountSessionCreateMutation) => {
      const adaptedData = getAdaptedAccountSessionCreate(data);

      if (adaptedData) {
        options.onCompleted?.(adaptedData);
      }
    },
  });

  if (featureFlag) {
    return [executeMutation, mutationResult];
  }

  return [executeLegacyMutation, legacyMutationResult];
};

type MeQueryHookResult = QueryResult<MeQuery, unknown>;

type UseAccountIdentityQueryOptions = Pick<
  Required<NonNullable<Parameters<typeof useQuery<MeQuery>>[1]>>,
  'fetchPolicy' | 'notifyOnNetworkStatusChange' | 'onCompleted' | 'onError'
>;

type UseAccountIdentityQueryResult = Pick<MeQueryHookResult, 'data' | 'error' | 'loading'> & {
  refetch: () => Promise<unknown>;
};

const useAccountIdentityQuery = (
  options: UseAccountIdentityQueryOptions
): UseAccountIdentityQueryResult => {
  const featureFlag = useFederationApiEppoFlag();
  const newQuery = useQuery<AccountIdentityQuery>(IdentityQuery, {
    ...options,
    errorPolicy: 'all',
    onCompleted: (data: AccountIdentityQuery) => {
      if (featureFlag) {
        const adaptedData = getAdaptedMeQuery(data);

        if (adaptedData) {
          options.onCompleted(adaptedData);
        }
      }
    },
    onError: error => {
      if (featureFlag) {
        const adaptedData =
          newQuery.data?.me === null ? getAdaptedMeQuery(newQuery.data) : undefined;

        if (adaptedData) {
          options.onCompleted(adaptedData);
        } else {
          options.onError(error);
        }
      }
    },
    skip: !featureFlag,
  });
  const legacyQuery = useQuery<MeQuery>(Me, {
    ...options,
    onCompleted: data => {
      if (!featureFlag) {
        options.onCompleted(data);
      }
    },
    skip: featureFlag,
  });

  if (featureFlag) {
    return {
      data: newQuery.data ? getAdaptedMeQuery(newQuery.data) : undefined,
      error: newQuery.error,
      loading: newQuery.loading,
      refetch: newQuery.refetch,
    };
  }

  return legacyQuery;
};

type UseIdentityQueryResult = Pick<MeQueryHookResult, 'error'> & {
  identity: MeQuery['me'];
  isLoading: MeQueryHookResult['loading'];
};

type UseIdentityQueryOptions = {
  isAuthRequired?: boolean;
};

const useAuth = ({isAuthRequired}: UseIdentityQueryOptions): UseIdentityQueryResult => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [isLoading, setIsLoading] = useState(true);

  const driverAssignmentMatch = useRouteMatch<{driverAssignmentId: string}>(PROGRESS_PATH);
  const driverAssignmentId = driverAssignmentMatch?.params?.driverAssignmentId;
  const selfAssignDeliveryMatch = useRouteMatch<{deliveryId: string}>(SELF_ASSIGN_PATH);
  const deliveryId = selfAssignDeliveryMatch?.params?.deliveryId;
  const source = searchParams.get('source') as SelfAssignSource | null;
  const isSelfAssign = !!deliveryId && (source === 'qrcode' || source === 'sms');

  const navigateToSignIn = (): void => {
    const redirectParams = isSelfAssign ? `?deliveryId=${deliveryId}&source=${source}` : '';
    history.push(`${REQUEST_SIGN_IN_LINK_PATH}${redirectParams}`);
  };

  const navigateToSelfAssignRegistration = (): void => {
    const redirectParams = source ? `?source=${source}` : '';
    history.push(`/self_assign_registration/${deliveryId}${redirectParams}`);
  };

  const navigateToExpiredMagicLink = (): void => {
    if (driverAssignmentId) {
      return history.push(`${EXPIRED_MAGIC_LINK_PATH}?driverAssignmentId=${driverAssignmentId}`);
    }

    if (deliveryId || source) {
      return history.push(`${EXPIRED_MAGIC_LINK_PATH}?deliveryId=${deliveryId}&source=${source}`);
    }

    history.push(EXPIRED_MAGIC_LINK_PATH);
  };

  const stripMagicLinkTokenFromUrl = (): void => {
    searchParams.delete(MAGIC_LINK_TOKEN_QUERY_PARAM);
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const [createMagicLinkSession, createMagicLinkSessionQueryResult] =
    useAccountSessionCreateMutation({
      onCompleted: result => {
        if (result.magicLinkDriverSessionCreate?.success) {
          meQueryResult.refetch();
        } else {
          navigateToExpiredMagicLink();
          setIsLoading(false);
        }
      },
      onError: () => {
        navigateToExpiredMagicLink();
        setIsLoading(false);
      },
    });

  const meQueryResult = useAccountIdentityQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({me}) => {
      const magicLinkToken = searchParams.get(MAGIC_LINK_TOKEN_QUERY_PARAM);
      const shouldCreateSession = (isAuthRequired || isSelfAssign) && !!magicLinkToken;
      // Due to the way QR codes are generated in ezrails, drivers can't be sent
      // a link that contains a magic link token. We account for that here by
      // directing them to the page to request a magic link that does contain a
      // token.
      const isMagicLinkNeeded = (isAuthRequired || isSelfAssign) && !magicLinkToken;

      configureUserMonitoring(me);

      if (magicLinkToken) {
        stripMagicLinkTokenFromUrl();
      }

      if (me?.id) {
        if (isSelfAssign && (!me.firstName || !me.lastName)) {
          navigateToSelfAssignRegistration();
        }
      } else if (shouldCreateSession) {
        createMagicLinkSession({variables: {input: {token: magicLinkToken}}});
        return;
      } else if (isMagicLinkNeeded) {
        navigateToSignIn();
      }

      setIsLoading(false);
    },
    onError: () => {
      if (searchParams.get(MAGIC_LINK_TOKEN_QUERY_PARAM)) {
        stripMagicLinkTokenFromUrl();
      }

      setIsLoading(false);
    },
  });

  return {
    error: meQueryResult.error || createMagicLinkSessionQueryResult.error,
    identity: meQueryResult.data?.me,
    isLoading,
  };
};

export default useAuth;
